import { ShieldCheckIcon } from '@phosphor-icons/react';
import { SERVICE_ART, ServiceSuggestionList } from '@voll-intelligence/intelligence-ui-kit';

import type { GenUiAction, GenUiDispatch } from '../../../shared/ui/action.js';
import { useGenUiI18n, type GenUiKey } from '../../../shared/ui/i18n.js';
import { nextServiceAsk, nextServicesNote } from '../next_services_contract.js';
import type {
  NextServiceId,
  NextServicesBlockMap,
  NextServicesProduct,
} from '@voll-intelligence/genui-contract';
import { BLOCK_ELEVATION } from '../../../shared/ui/block_elevation.js';

const SERVICE_LABEL: Record<NextServiceId, GenUiKey> = {
  hotel: 'nextServices.hotel',
  flight: 'nextServices.flight',
  seat: 'nextServices.seat',
  car: 'nextServices.car',
  train: 'nextServices.train',
  bus: 'nextServices.bus',
  insurance: 'nextServices.insurance',
  transfer_to_airport: 'nextServices.transferToAirport',
  transfer_airport_hotel: 'nextServices.transferAirportHotel',
  transfer_to_hotel: 'nextServices.transferToHotel',
};

/** As ILUSTRAÇÕES do catálogo de serviço do DS (renders 160×160, fundo branco) —
 *  a mesma família dos atalhos guiados. `insurance` é a exceção DO DESIGN: a
 *  story de serviços complementares usa o glifo de linha do escudo, não um
 *  render (não existe `seguro` no catálogo de ilustrações). Os traslados
 *  reusam o render do carro (`SERVICE_ART.rental`): o catálogo não tem um de
 *  traslado. */
const SERVICE_ART_BY_ID: Partial<Record<NextServiceId, string>> = {
  hotel: SERVICE_ART.hotel,
  flight: SERVICE_ART.flight,
  seat: SERVICE_ART.seat,
  car: SERVICE_ART.rental,
  train: SERVICE_ART.train,
  bus: SERVICE_ART.bus,
  transfer_to_airport: SERVICE_ART.rental,
  transfer_airport_hotel: SERVICE_ART.rental,
  transfer_to_hotel: SERVICE_ART.rental,
};

/** As linhas de traslado — o agendamento de carro da viagem. */
const TRANSFER_SERVICES: ReadonlySet<NextServiceId> = new Set<NextServiceId>([
  'transfer_to_airport',
  'transfer_airport_hotel',
  'transfer_to_hotel',
]);

/** A ação do toque numa linha: sempre o pedido visível (`reply`). O traslado
 *  leva também o domínio FORA do texto (`domainHint: 'mobility'`), e o
 *  supervisor entra direto no agendamento de carro em vez de classificar a
 *  prosa; as outras linhas vão sem a chave. */
function rowAction(service: NextServiceId, text: string): GenUiAction {
  return TRANSFER_SERVICES.has(service)
    ? { type: 'reply', text, domainHint: 'mobility' }
    : { type: 'reply', text };
}

/**
 * Serviços complementares pós-reserva, o mesmo card para todo produto (#2116,
 * #2349, frame 09 do ônibus, #5514), na lista de sugestões do kit — a mesma da
 * tela "Solicitação enviada". Cada linha manda o pedido do serviço na PRÓPRIA
 * thread (ação `reply`), e o supervisor roteia a prosa (o booked já soltou o
 * lock do domínio). O produto dita o pedido de cada linha e a nota
 * (`next_services_contract.ts`). Bloco sem `services` (os DATA-ONLY do
 * histórico de hotel e corrida) não desenha.
 */
export function NextServicesView<P extends NextServicesProduct>({
  product,
  ui,
  dispatch,
  readOnly = false,
}: Readonly<{
  product: P;
  ui: NextServicesBlockMap[P];
  dispatch: GenUiDispatch;
  readOnly?: boolean;
}>) {
  const { t, locale } = useGenUiI18n();
  const { services } = ui;
  if (!services) return null;
  return (
    <div data-testid={`${product}-next-services`} className={BLOCK_ELEVATION}>
      <ServiceSuggestionList
        suggestions={services.map(({ service, recommended, note }) => ({
          id: service,
          label: t(SERVICE_LABEL[service]),
          fact: note,
          art: SERVICE_ART_BY_ID[service],
          icon: <ShieldCheckIcon size={20} aria-hidden />,
          recommended,
        }))}
        copy={{ recommended: t('nextServices.recommended'), note: t(nextServicesNote(product)) }}
        disabled={readOnly}
        onSelect={(id) => {
          const service = services.find((s) => s.service === id)?.service;
          if (service)
            dispatch(rowAction(service, nextServiceAsk(product, service, ui, t, locale)));
        }}
      />
    </div>
  );
}
